import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';

// Definimos el tipo para una anotación individual
export interface Annotation {
  day: number;
  month: number;
  year: number;
  title: string;
  description: string;
  images: string[];
}

interface AnnotationModalProps {
  annotation: Annotation;
  onClose: () => void;
  position: { top: number; left: number };
  onDelete?: () => void; // Nueva prop para manejar el borrado
  disableDelete?: boolean;
  showAdminOption?: boolean; // Nueva prop para mostrar el botón de opción admin
  postId?: number; // Nuevo: id real del post
  userId?: number; // Nuevo: userId real del post
}

// Función para obtener la fecha de hoy en España (solo fecha, sin hora)
function getTodayInSpain() {
  const now = new Date();
  // Convertir a zona horaria de España (Europe/Madrid)
  const spainDate = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
  return spainDate;
}

// Función para saber si un post se puede borrar (fecha > hoy, solo fecha española)
function canDeletePost(postDateStr: string, isAdmin: boolean = false) {
  // Los usuarios ADMIN pueden borrar posts sin restricción de fecha
  if (isAdmin) {
    return true;
  }
  
  // Para usuarios normales, solo permitir borrar posts de fechas futuras (no hoy ni pasadas)
  // Usar la misma lógica que los webhooks: solo comparar fecha, no hora
  const today = getTodayInSpain();
  const postDate = new Date(postDateStr);
  
  // Convertir postDate a hora de España y extraer solo la fecha (sin hora)
  const postDateInSpain = new Date(postDate.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
  const todayInSpain = new Date(today.toLocaleString("en-US", {timeZone: "Europe/Madrid"}));
  
  // Comparar solo año, mes y día (ignorar horas)
  const postYear = postDateInSpain.getFullYear();
  const postMonth = postDateInSpain.getMonth();
  const postDay = postDateInSpain.getDate();
  const todayYear = todayInSpain.getFullYear();
  const todayMonth = todayInSpain.getMonth();
  const todayDay = todayInSpain.getDate();
  
  // Permitir borrar solo si la fecha del post es posterior a hoy
  if (postYear > todayYear) return true;
  if (postYear < todayYear) return false;
  if (postMonth > todayMonth) return true;
  if (postMonth < todayMonth) return false;
  return postDay > todayDay;
}

const AnnotationModal: React.FC<AnnotationModalProps> = ({ annotation, onClose, position, onDelete, disableDelete, showAdminOption, postId, userId }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [webhookStatus, setWebhookStatus] = useState<'idle' | 'loading' | 'success' | 'error'>("idle");
  const [webhookMessage, setWebhookMessage] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev === 0 ? annotation.images.length - 1 : prev - 1));
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev === annotation.images.length - 1 ? 0 : prev + 1));
  };

  const date = new Date(annotation.year, annotation.month, annotation.day);
  const formattedDate = date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Estilo para el posicionamiento del modal
  const modalStyle: React.CSSProperties = {
    position: 'absolute',
    top: `${position.top}px`,
    left: `${position.left}px`,
    transform: 'translate(-50%, 10px)', // Centrar y separar un poco
    zIndex: 50,
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div 
        ref={modalRef}
        className="bg-white rounded-3xl shadow-lg w-80 max-w-[90vw] font-futura relative"
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
    >
      {/* Contenedor de botones superiores */}
      <div className="absolute top-3 right-3 z-10 flex flex-row gap-2 items-center">
        {/* Botón editar solo para admin y si la fecha lo permite */}
        {showAdminOption && canDeletePost(new Date(annotation.year, annotation.month, annotation.day).toISOString(), true) && (
          <button
            className="w-8 h-8 flex items-center justify-center bg-gray-500/80 hover:bg-gray-600 rounded-full shadow-md text-white transition-all duration-200 backdrop-blur-sm"
            aria-label="Opción admin"
            title="Editar publicación"
            onClick={() => {
              // Si el admin está viendo como otro usuario, reenviar los datos de adminViewUser
              const adminViewUserId = location.state?.adminViewUserId;
              const adminViewUserEmail = location.state?.adminViewUserEmail;
              const adminViewUserPlan = location.state?.adminViewUserPlan;
              navigate('/create-content', {
                state: {
                  ...(postId ? { postId } : {}),
                  title: annotation.title,
                  description: annotation.description,
                  images: annotation.images,
                  postDate: new Date(annotation.year, annotation.month, annotation.day).toISOString(),
                  ...(userId ? { userId } : {}),
                  originYear: annotation.year,
                  originMonth: annotation.month,
                  ...(adminViewUserId ? {
                    adminViewUserId,
                    adminViewUserEmail,
                    adminViewUserPlan
                  } : {})
                }
              });
            }}
          >
            {/* Icono editar igual que en AdminUserListScreen */}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
        {/* Botón de envío a webhook solo para admin, sin restricción de fecha */}
        {showAdminOption && (
          <>
            <button
              className="w-8 h-8 flex items-center justify-center bg-blue-500/80 hover:bg-blue-600 rounded-full shadow-md text-white transition-all duration-200 backdrop-blur-sm"
              aria-label="Enviar a webhook"
              title="Enviar a webhook"
              disabled={webhookStatus === 'loading'}
              onClick={() => setShowConfirmModal(true)}
            >
              {/* Icono de nube con flecha */}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01.88-7.903A5.002 5.002 0 0117 9a5 5 0 01.17 9.995H7a4 4 0 010-8zm5 4v-4m0 0l-2 2m2-2l2 2" />
              </svg>
            </button>
            {/* Modal de confirmación */}
            <ConfirmModal
              isOpen={showConfirmModal}
              onClose={() => setShowConfirmModal(false)}
              onConfirm={async () => {
                setShowConfirmModal(false);
                if (!postId) return;
                setWebhookStatus('loading');
                setWebhookMessage('');
                try {
                  // Detectar plan del usuario
                  const plan = location.state?.adminViewUserPlan || '';
                  const token = localStorage.getItem('token');
                  let endpoint = '';
                  if (plan.startsWith('RRSS')) {
                    endpoint = '/webhook/rrss/send-post';
                  } else if (plan.startsWith('SEO')) {
                    endpoint = '/webhook/seo/send-post';
                  } else {
                    setWebhookStatus('error');
                    setWebhookMessage('No se puede determinar el tipo de plan para este usuario.');
                    return;
                  }
                  const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ postId })
                  });
                  if (res.ok) {
                    setWebhookStatus('success');
                    setWebhookMessage('¡Publicado!');
                  } else {
                    const data = await res.json().catch(() => ({}));
                    setWebhookStatus('error');
                    setWebhookMessage(data?.error || 'Error al enviar al webhook');
                  }
                } catch (err) {
                  setWebhookStatus('error');
                  setWebhookMessage('Error de red al enviar al webhook');
                }
              }}
              message={<span>¿Seguro que quieres publicar este post?</span>}
              confirmText="Enviar"
              cancelText="Cancelar"
              type="info"
              isLoading={webhookStatus === 'loading'}
              publicationTitle={annotation.title}
              subtitle="Esta acción enviará los datos de la publicación al sistema externo."
            />
          </>
        )}
        {/* Feedback visual del webhook */}
        {webhookStatus !== 'idle' && (
          <span className={`ml-2 text-xs font-semibold ${webhookStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>{webhookMessage}</span>
        )}
        {/* Botón de borrar */}
        {onDelete && !disableDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="w-8 h-8 flex items-center justify-center bg-red-500/80 hover:bg-red-500 rounded-full shadow-md text-white hover:text-white transition-all duration-200 backdrop-blur-sm"
            aria-label="Borrar publicación"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
        {/* Botón de cerrar */}
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center bg-white/80 hover:bg-white rounded-full shadow-md text-gray-500 hover:text-gray-700 transition-all duration-200 backdrop-blur-sm"
          aria-label="Cerrar"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Carrusel de imágenes o icono si no hay */}
      <div className="relative">
        {annotation.images && annotation.images.length > 0 ? (
          (() => {
            const imgSrc = annotation.images[0];
            if (imgSrc.startsWith('data:')) {
              return (
                <img src={imgSrc} alt={annotation.title} className="w-full h-48 object-cover rounded-t-3xl" />
              );
            } else if (imgSrc.startsWith('/uploads/post-images/')) {
              // Imagen local del servidor - construir URL completa
              const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:3001';
              const fullUrl = `${baseUrl}${imgSrc}`;
              console.log('🔍 AnnotationModal - Imagen local:', { imgSrc, fullUrl, baseUrl, originalApiUrl: process.env.REACT_APP_API_URL });
              return (
                <img 
                  src={fullUrl} 
                  alt={annotation.title} 
                  className="w-full h-48 object-cover rounded-t-3xl"
                  onError={(e) => {
                    console.error('❌ Error cargando imagen local en modal:', fullUrl, e);
                  }}
                  onLoad={() => {
                    console.log('✅ Imagen local cargada correctamente en modal:', fullUrl);
                  }}
                />
              );
            } else if (imgSrc.startsWith('http')) {
              return (
                <img src={imgSrc} alt={annotation.title} className="w-full h-48 object-cover rounded-t-3xl" />
              );
            } else {
              return (
                <div className="w-full h-48 flex items-center justify-center bg-[#e6f2fd] rounded-t-3xl">
                  <svg width="56" height="56" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="6" y="10" width="36" height="28" rx="4" stroke="#44aaf6" strokeWidth="2.5" fill="#e6f2fd" />
                    <path d="M14 30L20 24L28 32L34 26L40 32" stroke="#44aaf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="16.5" cy="18.5" r="2.5" fill="#44aaf6" />
                  </svg>
                </div>
              );
            }
          })()
        ) : (
          <div className="w-full h-48 flex items-center justify-center bg-[#e6f2fd] rounded-t-3xl">
            <svg width="56" height="56" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="10" width="36" height="28" rx="4" stroke="#44aaf6" strokeWidth="2.5" fill="#e6f2fd" />
              <path d="M14 30L20 24L28 32L34 26L40 32" stroke="#44aaf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="16.5" cy="18.5" r="2.5" fill="#44aaf6" />
            </svg>
          </div>
        )}
      </div>

      {/* Contenido */}
      <div className="p-5 max-w-full overflow-x-hidden overflow-y-auto" style={{maxHeight: '60vh'}}>
        {/* Etiqueta de generado automáticamente encima del título */}
        {annotation.images && annotation.images.length === 2 && annotation.images[1] === '' && (
          <div className="font-futura text-xs font-semibold mb-1" style={{ color: '#44aaf6', letterSpacing: '0.04em' }}>
            [ Generado automáticamente ]
          </div>
        )}
        <h3 className="font-bold text-lg text-gray-900 break-words max-w-full">{annotation.title}</h3>
        <p className="text-sm text-gray-500 mb-3 break-words max-w-full">{formattedDate}</p>
        <p className="text-sm text-gray-700 leading-relaxed break-words max-w-full whitespace-pre-line">{annotation.description}</p>
      </div>
    </div>
  );
};

export default AnnotationModal; 